import { Component, OnInit, AfterViewChecked, ViewChildren, ElementRef, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { AvatarModule } from 'primeng/avatar';
import { DividerModule } from 'primeng/divider';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from 'primeng/progressbar';
import { ChartModule } from 'primeng/chart';
import { TabsModule } from 'primeng/tabs';
import { CheckboxModule } from 'primeng/checkbox';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { UserService } from '../../layout/service/user.service';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-user-profile',
    standalone: true,
    imports: [
        CommonModule,
        CardModule,
        ButtonModule,
        InputTextModule,
        AvatarModule,
        DividerModule,
        TagModule,
        ProgressBarModule,
        ChartModule,
        TabsModule,
        CheckboxModule,
        SkeletonModule,
        ToastModule,
        ConfirmDialogModule,
        TooltipModule,
        FormsModule
    ],
    providers: [MessageService, ConfirmationService],
    styles: [`
        :host ::ng-deep .p-tabview .p-tabview-nav li .p-tabview-nav-link {
            border-radius: 12px 12px 0 0;
            margin-right: 4px;
            font-weight: 600;
        }
        
        :host ::ng-deep .p-tabview .p-tabview-panels {
            background: transparent;
            border: none;
            padding: 2rem 0 0 0;
        }
        
        :host ::ng-deep .p-progressbar {
            border-radius: 50px;
            overflow: hidden;
            background-color: #e5e7eb;
        }
        
        :host ::ng-deep .p-progressbar .p-progressbar-value {
            border-radius: 50px;
        }
        
        :host ::ng-deep .progress-excellent .p-progressbar-value {
            background: linear-gradient(90deg, #10b981, #059669);
        }
        
        :host ::ng-deep .progress-good .p-progressbar-value {
            background: linear-gradient(90deg, #3b82f6, #2563eb);
        }
        
        :host ::ng-deep .progress-average .p-progressbar-value {
            background: linear-gradient(90deg, #f59e0b, #d97706);
        }
        
        :host ::ng-deep .progress-poor .p-progressbar-value {
            background: linear-gradient(90deg, #ef4444, #dc2626);
        }
        
        :host ::ng-deep .p-inputtext:disabled {
            opacity: 0.6;
        }
        
        .glass-effect {
            background: rgba(255, 255, 255, 0.8);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.2);
        }
    `],
    template: `
        <p-toast></p-toast>
        <p-confirmDialog></p-confirmDialog>
        <div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-100 p-4 md:p-6">
            <div class="max-w-7xl mx-auto">
                <!-- Header Section -->
                <div class="bg-white rounded-2xl shadow-xl border border-blue-100 p-6 md:p-8 mb-8">
                    <div class="flex flex-col md:flex-row items-center gap-6">
                        <!-- Avatar Section -->
                        <div class="relative">
                            <div class="w-32 h-32 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                                {{ getInitials() }}
                            </div>
                            <div class="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-white flex items-center justify-center">
                                <i class="pi pi-check text-white text-xs"></i>
                            </div>
                        </div>

                        <!-- User Info -->
                        <div class="flex-1 text-center md:text-left">
                            <h1 class="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{{ userProfile.name }}</h1>
                            <p class="text-lg text-gray-600 mb-3">{{ userProfile.email }}</p>
                            <div class="flex flex-wrap gap-2 justify-center md:justify-start">
                                <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                                      [ngClass]="getRoleClasses(userProfile.role)">
                                    <i class="pi pi-user mr-2"></i>
                                    {{ userProfile.role }}
                                </span>
                                <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                    <i class="pi pi-building mr-2"></i>
                                    {{ userProfile.institution || 'Tidak ada institusi' }}
                                </span>
                            </div>
                        </div>

                        <!-- Quick Stats -->
                        <div class="grid grid-cols-2 gap-4 text-center">
                            <div class="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                                <div class="text-2xl font-bold text-green-700">{{ userStats.totalExams }}</div>
                                <div class="text-sm text-green-600">Total Ujian</div>
                            </div>
                            <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                                <div class="text-2xl font-bold text-blue-700">{{ formatScore(userStats.averageScore) }}%</div>
                                <div class="text-sm text-blue-600">Rata-rata Skor</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tabs Section -->
                <p-tabs value="0">
                    <p-tablist>
                        <p-tab value="0">
                            <i class="pi pi-user mr-2"></i>Informasi Profil
                        </p-tab>
                        <p-tab value="1">
                            <i class="pi pi-cog mr-2"></i>Pengaturan Section
                        </p-tab>
                        <p-tab value="2">
                            <i class="pi pi-chart-line mr-2"></i>Statistik Pembelajaran
                        </p-tab>
                        <p-tab value="3">
                            <i class="pi pi-shield mr-2"></i>Keamanan & Perangkat
                        </p-tab>
                    </p-tablist>
                    <p-tabpanels>
                        <p-tabpanel value="0">
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <!-- Profile Form -->
                            <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <div class="flex items-center justify-between mb-6">
                                    <h3 class="text-xl font-semibold text-gray-900">Data Pribadi</h3>
                                    <p-button 
                                        *ngIf="!isEditing"
                                        icon="pi pi-pencil" 
                                        [text]="true"
                                        [rounded]="true"
                                        severity="secondary"
                                        (onClick)="toggleEdit()"
                                        pTooltip="Edit Profile">
                                    </p-button>
                                </div>

                                <div class="space-y-6">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Nama Lengkap</label>
                                        <input 
                                            pInputText 
                                            type="text" 
                                            [(ngModel)]="userProfile.name"
                                            [disabled]="!isEditing"
                                            class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            [class.bg-gray-50]="!isEditing" />
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                        <input 
                                            pInputText 
                                            type="email" 
                                            [(ngModel)]="userProfile.email"
                                            [disabled]="!isEditing"
                                            class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            [class.bg-gray-50]="!isEditing" />
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Username</label>
                                        <input 
                                            pInputText 
                                            type="text" 
                                            [(ngModel)]="userProfile.username"
                                            [disabled]="!isEditing"
                                            class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            [class.bg-gray-50]="!isEditing" />
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Nomor Telepon</label>
                                        <input 
                                            pInputText 
                                            type="text" 
                                            [(ngModel)]="userProfile.phone"
                                            [disabled]="!isEditing"
                                            class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            [class.bg-gray-50]="!isEditing"
                                            placeholder="Masukkan nomor telepon" />
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Institusi</label>
                                        <input 
                                            pInputText 
                                            type="text" 
                                            [(ngModel)]="userProfile.institution"
                                            [disabled]="!isEditing"
                                            class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            [class.bg-gray-50]="!isEditing"
                                            placeholder="Masukkan nama institusi" />
                                    </div>

                                    <div *ngIf="isEditing" class="flex gap-3 pt-4 border-t">
                                        <p-button 
                                            label="Simpan Perubahan" 
                                            icon="pi pi-check" 
                                            (onClick)="saveProfile()"
                                            class="flex-1"
                                            [loading]="saving">
                                        </p-button>
                                        <p-button 
                                            label="Batal" 
                                            icon="pi pi-times" 
                                            (onClick)="cancelEdit()"
                                            severity="secondary"
                                            [outlined]="true">
                                        </p-button>
                                    </div>
                                </div>
                            </div>

                            <!-- Account Info -->
                            <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <h3 class="text-xl font-semibold text-gray-900 mb-6">Informasi Akun</h3>
                                
                                <div class="space-y-4">
                                    <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div class="flex items-center gap-3">
                                            <i class="pi pi-id-card text-blue-500 text-xl"></i>
                                            <div>
                                                <div class="font-medium text-gray-900">User ID</div>
                                                <div class="text-sm text-gray-600">{{ userProfile.userId }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div class="flex items-center gap-3">
                                            <i class="pi pi-shield text-green-500 text-xl"></i>
                                            <div>
                                                <div class="font-medium text-gray-900">Role</div>
                                                <div class="text-sm text-gray-600">{{ userProfile.role }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div class="flex items-center gap-3">
                                            <i class="pi pi-list text-purple-500 text-xl"></i>
                                            <div>
                                                <div class="font-medium text-gray-900">Akses Stasiun</div>
                                                <div class="text-sm text-gray-600">{{ getSectionNames() }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div class="flex items-center gap-3">
                                            <i class="pi pi-calendar text-orange-500 text-xl"></i>
                                            <div>
                                                <div class="font-medium text-gray-900">Bergabung Sejak</div>
                                                <div class="text-sm text-gray-600">{{ getJoinDate() }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </p-tabpanel>

                    <p-tabpanel value="1">
                        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">

                            <h3 class="text-xl font-semibold text-gray-900 mb-4">Pengaturan Section</h3>

                            <div class="mb-4 flex items-center gap-3">
                                <label class="text-sm text-gray-700">Timer Custom Semua (menit)</label>
                                <input pInputText type="number" min="0" class="w-32 p-2 border rounded" [(ngModel)]="selectedCustomTimerAll" [placeholder]="customTimerMixed ? 'Bervariasi' : 'Masukkan menit'" />
                                <p-tag *ngIf="customTimerMixed" value="Bervariasi" severity="warning"></p-tag>
                            </div>

                            <div *ngIf="sectionsLoading" class="text-center py-8">
                                <p-skeleton height="2rem" width="50%" class="mx-auto mb-4"></p-skeleton>
                                <p class="text-sm text-gray-500">Memuat daftar section...</p>
                            </div>

                                <div *ngIf="!sectionsLoading">
                                    <!-- Preview selected sections -->
                                    <div *ngIf="selectedSectionIds.length > 0" class="mb-4 bg-white p-3 rounded-lg border">
                                        <div class="flex items-center justify-between mb-2">
                                            <div class="font-medium">Preview Section Terpilih ({{ selectedSectionIds.length }})</div>
                                            <div class="text-sm text-gray-500">Klik nama untuk hapus</div>
                                        </div>
                                        <div class="flex flex-wrap gap-2">
                                            <button *ngFor="let sid of selectedSectionIds" type="button" class="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm border" (click)="onSectionToggle(false, sid)">{{ getSectionNameById(sid) }}</button>
                                        </div>
                                    </div>
                                <div *ngIf="sectionsList.length === 0" class="text-center py-8 text-gray-500">
                                    Tidak ada section tersedia.
                                </div>

                                <div *ngIf="sectionsList.length > 0" class="space-y-6">
                                    <ng-container *ngFor="let cat of getCategoryList()">
                                        <div class="bg-gray-50 p-3 rounded-lg border">
                                            <div class="flex items-center justify-between">
                                                <div class="font-semibold text-gray-800">{{ cat }}</div>
                                                <div class="flex items-center gap-3">
                                                    <label class="text-sm text-gray-600">Pilih semua</label>
                                                    <input #categoryAll data-cat="{{cat}}" type="checkbox" [checked]="isAllSelectedInCategory(cat)" (change)="toggleSelectAllInCategory(cat)" />
                                                </div>
                                            </div>

                                            <div class="mt-3 space-y-3">
                                                <div *ngFor="let s of getSectionsByCategory(cat)" class="flex items-start gap-3 p-3 border rounded-lg hover:bg-white">
                                                    <div class="pt-1">
                                                        <p-checkbox [binary]="true" [ngModel]="isSectionSelected(s.ID)" (ngModelChange)="onSectionToggle($event, s.ID)"></p-checkbox>
                                                    </div>
                                                    <div class="flex-1">
                                                        <div class="flex items-center justify-between">
                                                            <div>
                                                                <div class="font-medium text-gray-800">{{ s.NAME_STASIUN || s.NAMA_STASIUN || s.NAME || 'Section' }}</div>
                                                                <div class="text-xs text-gray-500">Urutan: {{ s.URUTAN }}</div>
                                                            </div>
                                                            <div class="text-xs text-gray-500 text-right">
                                                                <div *ngIf="s.CREATED_BY">Oleh: {{ s.CREATED_BY }}</div>
                                                                <div *ngIf="s.CREATED_AT">{{ s.CREATED_AT }}</div>
                                                            </div>
                                                        </div>

                                                        <div class="mt-2 flex flex-wrap gap-2">
                                                            <p-tag *ngIf="s.TIMER" [value]="'UKNPDPD: ' + formatSecondsToMinutes(s.TIMER)" severity="info"></p-tag>
                                                            <p-tag *ngIf="s.TIMER_PREKLINIK" [value]="'Preklinik: ' + formatSecondsToMinutes(s.TIMER_PREKLINIK)" severity="warning"></p-tag>
                                                            <p-tag *ngIf="s.TIMER_KOAS" [value]="'Koas: ' + formatSecondsToMinutes(s.TIMER_KOAS)" severity="success"></p-tag>
                                                            <p-tag *ngIf="s.TIMER_CUSTOM" [value]="'Custom: ' + formatSecondsToMinutes(s.TIMER_CUSTOM)" severity="danger"></p-tag>
                                                        </div>

                                                        <!-- Custom timer now controlled via the global input above -->
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </ng-container>
                                </div>

                                <div class="flex gap-3 justify-end mt-4">
                                    <p-button label="Batal" severity="secondary" (onClick)="cancelSectionChanges()" [disabled]="savingSections"></p-button>
                                    <p-button label="Simpan" icon="pi pi-check" (onClick)="saveSectionSettings()" [loading]="savingSections"></p-button>
                                </div>
                            </div>
                        </div>
                    </p-tabpanel>

                    <p-tabpanel value="2">
                        <!-- Loading State -->
                        <div *ngIf="loading" class="space-y-6">
                            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div *ngFor="let i of [1,2,3]" class="bg-white rounded-2xl p-6 shadow-lg">
                                    <p-skeleton height="4rem" width="100%" class="mb-3"></p-skeleton>
                                    <p-skeleton height="2rem" width="70%"></p-skeleton>
                                </div>
                            </div>
                            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div *ngFor="let i of [1,2]" class="bg-white rounded-2xl p-6 shadow-lg">
                                    <p-skeleton height="2rem" width="60%" class="mb-4"></p-skeleton>
                                    <div class="space-y-3">
                                        <p-skeleton *ngFor="let j of [1,2,3]" height="3rem" width="100%"></p-skeleton>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Content -->
                        <div *ngIf="!loading">
                            <!-- No Data State -->
                            <div *ngIf="userStats.totalExams === 0 && categoryStats.length === 0 && recentActivities.length === 0" class="text-center py-16">
                                <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 max-w-md mx-auto">
                                    <i class="pi pi-chart-line text-6xl text-gray-300 mb-6 block"></i>
                                    <h3 class="text-xl font-semibold text-gray-700 mb-3">Belum Ada Data Pembelajaran</h3>
                                    <p class="text-gray-500 mb-6">Mulai pembelajaran untuk melihat statistik dan progress Anda di sini</p>
                                    <div class="flex flex-col sm:flex-row gap-3 justify-center">
                                        <p-button 
                                            label="Mulai Anatomi" 
                                            icon="pi pi-play" 
                                            severity="primary"
                                            [outlined]="true"
                                            size="small">
                                        </p-button>
                                        <p-button 
                                            label="OSCE Sheet" 
                                            icon="pi pi-file" 
                                            severity="secondary"
                                            [outlined]="true"
                                            size="small">
                                        </p-button>
                                        <p-button 
                                            label="Simulasi" 
                                            icon="pi pi-desktop" 
                                            severity="help"
                                            [outlined]="true"
                                            size="small">
                                        </p-button>
                                    </div>
                                </div>
                            </div>

                            <!-- Data Available -->
                            <div *ngIf="userStats.totalExams > 0 || categoryStats.length > 0 || recentActivities.length > 0">
                                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                            <!-- Overview Cards -->
                            <div class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
                                <div class="flex items-center justify-between">
                                    <div>
                                        <h3 class="text-lg font-semibold mb-2">Total Ujian</h3>
                                        <p class="text-3xl font-bold">{{ userStats.totalExams }}</p>
                                        <p class="text-blue-100 text-sm">Semua jenis ujian</p>
                                    </div>
                                    <i class="pi pi-file-check text-4xl text-blue-200"></i>
                                </div>
                            </div>

                            <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
                                <div class="flex items-center justify-between">
                                    <div>
                                        <h3 class="text-lg font-semibold mb-2">Ujian Selesai</h3>
                                        <p class="text-3xl font-bold">{{ userStats.completedExams }}</p>
                                        <p class="text-green-100 text-sm">{{ formatScore(getCompletionRate()) }}% tingkat penyelesaian</p>
                                    </div>
                                    <i class="pi pi-check-circle text-4xl text-green-200"></i>
                                </div>
                            </div>

                            <div class="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
                                <div class="flex items-center justify-between">
                                    <div>
                                        <h3 class="text-lg font-semibold mb-2">Rata-rata Skor</h3>
                                        <p class="text-3xl font-bold">{{ formatScore(userStats.averageScore) }}%</p>
                                        <p class="text-purple-100 text-sm" [ngClass]="getScoreClass()">{{ getScoreLabel() }}</p>
                                    </div>
                                    <i class="pi pi-star text-4xl text-purple-200"></i>
                                </div>
                            </div>
                        </div>

                        <!-- Detailed Statistics -->
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <!-- Performance by Category -->
                            <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <h3 class="text-xl font-semibold text-gray-900 mb-6">Performa per Kategori</h3>
                                
                                <!-- No Data State -->
                                <div *ngIf="categoryStats.length === 0" class="text-center py-8">
                                    <i class="pi pi-chart-bar text-4xl text-gray-300 mb-4 block"></i>
                                    <p class="text-gray-500 text-sm">Belum ada data performa</p>
                                    <p class="text-gray-400 text-xs mt-1">Mulai ujian untuk melihat statistik performa Anda</p>
                                </div>
                                
                                <!-- Data Available -->
                                <div *ngIf="categoryStats.length > 0" class="space-y-4">
                                    <div *ngFor="let category of categoryStats" class="space-y-2">
                                        <div class="flex justify-between items-center">
                                            <span class="font-medium text-gray-700">{{ category.name }}</span>
                                            <span class="text-sm font-semibold" [ngClass]="getScoreColorClass(category.score)">
                                                {{ formatScore(category.score) }}%
                                            </span>
                                        </div>
                                        <p-progressBar 
                                            [value]="category.score" 
                                            [style]="{'height': '8px'}"
                                            [showValue]="false"
                                            [class]="'progress-' + getScoreLevel(category.score)">
                                        </p-progressBar>
                                        <div class="text-xs text-gray-500">{{ category.attempts }} ujian</div>
                                    </div>
                                </div>
                            </div>

                            <!-- Recent Activity -->
                            <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <h3 class="text-xl font-semibold text-gray-900 mb-6">Aktivitas Terbaru</h3>
                                
                                <!-- No Data State -->
                                <div *ngIf="recentActivities.length === 0" class="text-center py-8">
                                    <i class="pi pi-history text-4xl text-gray-300 mb-4 block"></i>
                                    <p class="text-gray-500 text-sm">Belum ada aktivitas</p>
                                    <p class="text-gray-400 text-xs mt-1">Aktivitas pembelajaran Anda akan muncul di sini</p>
                                </div>
                                
                                <!-- Data Available -->
                                <div *ngIf="recentActivities.length > 0" class="space-y-4">
                                    <div *ngFor="let activity of recentActivities" class="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div class="w-2 h-2 rounded-full mt-3" [ngClass]="getActivityColor(activity.type)"></div>
                                        <div class="flex-1">
                                            <div class="font-medium text-gray-900">{{ activity.title }}</div>
                                            <div class="text-sm text-gray-600">{{ activity.description }}</div>
                                            <div class="text-xs text-gray-500 mt-1">{{ formatDate(activity.date) }}</div>
                                        </div>
                                        <div *ngIf="activity.score" class="text-sm font-semibold" [ngClass]="getScoreColorClass(activity.score)">
                                            {{ formatScore(activity.score) }}%
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                            </div>
                        </div>
                    </p-tabpanel>

                    <p-tabpanel value="3">
                        <div class="space-y-6">
                            <!-- Logged-in Devices Section -->
                            <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <div class="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 class="text-xl font-semibold text-gray-900">Perangkat yang Masuk</h3>
                                        <p class="text-sm text-gray-600 mt-1">Kelola perangkat yang dapat mengakses akun Anda</p>
                                    </div>
                                    <p-button 
                                        label="Keluar dari Semua Perangkat" 
                                        icon="pi pi-sign-out" 
                                        severity="danger"
                                        [outlined]="true"
                                        size="small"
                                        (onClick)="logoutFromAllDevices()"
                                        [disabled]="userProfile.devices.length === 0">
                                    </p-button>
                                </div>

                                <!-- No Devices State -->
                                <div *ngIf="userProfile.devices.length === 0" class="text-center py-8">
                                    <i class="pi pi-desktop text-4xl text-gray-300 mb-4 block"></i>
                                    <p class="text-gray-500 text-sm">Tidak ada perangkat yang terdaftar</p>
                                </div>

                                <!-- Devices List -->
                                <div *ngIf="userProfile.devices.length > 0" class="space-y-4">
                                    <div *ngFor="let device of userProfile.devices" 
                                         class="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div class="flex items-center gap-4">
                                            <!-- Device Icon -->
                                            <div class="w-12 h-12 rounded-full flex items-center justify-center"
                                                 [ngClass]="getDeviceIconClass(device.DEVICE_INFO)">
                                                <i [class]="getDeviceIcon(device.DEVICE_INFO)" class="text-xl"></i>
                                            </div>
                                            
                                            <!-- Device Info -->
                                            <div class="flex-1">
                                                <div class="flex items-center gap-2">
                                                    <h4 class="font-medium text-gray-900">{{ getDeviceName(device.DEVICE_INFO) }}</h4>
                                                    <span *ngIf="isCurrentDevice(device.DEVICE_ID)" 
                                                          class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <i class="pi pi-circle-fill mr-1 text-xs"></i>
                                                        Perangkat Saat Ini
                                                    </span>
                                                </div>
                                                <p class="text-sm text-gray-600">{{ getBrowserInfo(device.DEVICE_INFO) }}</p>
                                                <p class="text-xs text-gray-500">
                                                    <i class="pi pi-clock mr-1"></i>
                                                    Terakhir masuk: {{ formatLastLogin(device.LAST_LOGIN) }}
                                                </p>
                                                <p class="text-xs text-gray-400 mt-1 font-mono">
                                                    ID: {{ device.DEVICE_ID.substring(0, 16) }}...
                                                </p>
                                            </div>
                                        </div>

                                        <!-- Actions -->
                                        <div class="flex items-center gap-2">
                                            <p-button 
                                                *ngIf="!isCurrentDevice(device.DEVICE_ID)"
                                                icon="pi pi-sign-out" 
                                                severity="danger"
                                                [text]="true"
                                                [rounded]="true"
                                                size="small"
                                                (onClick)="removeDevice(device)"
                                                pTooltip="Keluarkan Perangkat">
                                            </p-button>
                                            <p-button 
                                                *ngIf="isCurrentDevice(device.DEVICE_ID)"
                                                icon="pi pi-info-circle" 
                                                severity="info"
                                                [text]="true"
                                                [rounded]="true"
                                                size="small"
                                                [disabled]="true"
                                                pTooltip="Perangkat Saat Ini">
                                            </p-button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Security Tips -->
                                <div class="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div class="flex items-start gap-3">
                                        <i class="pi pi-info-circle text-blue-500 text-lg mt-0.5"></i>
                                        <div>
                                            <h4 class="font-medium text-blue-900 mb-1">Tips Keamanan</h4>
                                            <ul class="text-sm text-blue-700 space-y-1">
                                                <li>• Selalu keluar dari akun Anda di perangkat umum</li>
                                                <li>• Hapus perangkat yang tidak dikenali dari daftar ini</li>
                                                <li>• Ganti password jika melihat aktivitas mencurigakan</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </p-tabpanel>
                </p-tabpanels>
            </p-tabs>
            </div>
        </div>
    `
})
export class UserProfileComponent implements OnInit, AfterViewChecked {
    userProfile = {
        userId: '',
        name: '',
        email: '',
        username: '',
        role: '',
        phone: '',
        institution: '',
        listSections: [] as string[],
        accessRole: '',
        createdAt: '',
        isActive: false,
        devices: [] as any[]
    };

    userStats = {
        totalExams: 0,
        completedExams: 0,
        averageScore: 0,
        anatomyCount: 0,
        sheetCount: 0,
        simulationCount: 0
    };

    categoryStats: any[] = [];
    recentActivities: any[] = [];
    // Sections settings
    sectionsList: any[] = [];
    selectedSectionIds: string[] = [];
    // per-section custom timer values (in minutes)
    selectedCustomTimers: { [id: string]: number } = {};
    // single custom timer input to apply to all sections (minutes)
    selectedCustomTimerAll: number = 0;
    // whether per-section custom timers vary across sections
    customTimerMixed: boolean = false;
    sectionsLoading: boolean = false;
    savingSections: boolean = false;
    @ViewChildren('categoryAll') categoryAllCheckboxes!: QueryList<ElementRef<HTMLInputElement>>;
    
    originalProfile: any;
    isEditing = false;
    saving = false;
    loading = true;

    constructor(
        private userService: UserService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {}

    async ngOnInit() {
        this.loading = true;
        await this.loadUserProfile();
        // load available sections for settings tab
        this.loadUserSections();
        try {
            await Promise.all([
                this.loadUserStats(),
                this.loadRecentActivities()
            ]);
        } catch (error) {
            console.error('Error loading profile data:', error);
        } finally {
            this.loading = false;
        }
    }

    async loadUserProfile() {
        try {
            // Get user ID from localStorage
            const userData = localStorage.getItem('user');
            let userId = '';
            
            if (userData) {
                const user = JSON.parse(userData);
                userId = user.ID_USER || '';
            }

            if (!userId) {
                console.error('No user ID found in localStorage');
                this.setDefaultProfile();
                return;
            }

            // Fetch user data from API using getUserById
            const response = await this.userService.getUserById({ ID_USER: userId });
            console.log('getUserById response:', response);
            
            if (response.status && response.data) {
                const userData = response.data;
                this.userProfile = {
                    userId: userData.ID_USER || '',
                    name: userData.FULLNAME || 'User Name',
                    email: userData.EMAIL || '-',
                    username: userData.USERNAME || 'username',
                    role: userData.NAME_ACCESS_ROLE || 'Peserta',
                    phone: userData.PHONE || '',
                    institution: userData.INSTANSI || '',
                    listSections: userData.LIST_SECTION ? JSON.parse(userData.LIST_SECTION) : [],
                    accessRole: userData.ID_ACCESS_ROLE || '',
                    createdAt: userData.CREATED_AT || '',
                    isActive: userData.IS_ACTIVED === '1',
                    devices: userData.DEVICES || []
                };
                
                // Update localStorage with fresh data
                localStorage.setItem('user', JSON.stringify(userData));
            } else {
                console.error('Failed to fetch user data:', response.message);
                this.setDefaultProfile();
            }
        } catch (error) {
            console.error('Error loading user profile:', error);
            this.messageService.add({
                severity: 'warn',
                summary: 'Peringatan',
                detail: 'Gagal memuat data profil dari server. Menggunakan data lokal.'
            });
            this.setDefaultProfile();
        }
        
        this.originalProfile = { ...this.userProfile };
    }

    setDefaultProfile() {
        // Default values if no user data
        this.userProfile = {
            userId: '',
            name: 'User Name',
            email: 'user@example.com',
            username: 'username',
            role: 'Peserta',
            phone: '',
            institution: '',
            listSections: [],
            accessRole: '',
            createdAt: '',
            isActive: false,
            devices: []
        };
    }

    async loadUserStats() {
        try {
            // Load data from different sources
            const [anatomyData, sheetData, simulationData] = await Promise.all([
                this.loadAnatomyHistory(),
                this.loadSheetHistory(),
                this.loadSimulationHistory()
            ]);

            // Calculate statistics
            this.userStats = {
                totalExams: anatomyData.count + sheetData.count + simulationData.count,
                completedExams: anatomyData.completed + sheetData.completed + simulationData.completed,
                averageScore: this.calculateOverallAverage([anatomyData, sheetData, simulationData]),
                anatomyCount: anatomyData.count,
                sheetCount: sheetData.count,
                simulationCount: simulationData.count
            };

            // Set category stats
            this.categoryStats = [
                { name: 'Anatomi', score: Math.round((anatomyData.averageScore || 0) * 100) / 100, attempts: anatomyData.count },
                { name: 'OSCE Sheet', score: Math.round((sheetData.averageScore || 0) * 100) / 100, attempts: sheetData.count },
                { name: 'Simulasi', score: Math.round((simulationData.averageScore || 0) * 100) / 100, attempts: simulationData.count }
            ].filter(cat => cat.attempts > 0);

        } catch (error) {
            console.error('Error loading user stats:', error);
            // Keep empty stats to show proper empty states
            this.userStats = {
                totalExams: 0,
                completedExams: 0,
                averageScore: 0,
                anatomyCount: 0,
                sheetCount: 0,
                simulationCount: 0
            };

            this.categoryStats = [];
        }
    }

    async loadAnatomyHistory(): Promise<any> {
        try {
            // Get user ID from localStorage
            const userData = localStorage.getItem('user');
            const user = userData ? JSON.parse(userData) : null;
            const userId = user?.ID_USER;

            if (!userId) {
                return { count: 0, completed: 0, averageScore: 0, data: [] };
            }

            // Get anatomy answers for this user
            const response = await this.userService.getAnatomyAnswers({ user_id: userId });
            const data = response.data || [];
            
            // Calculate average score from anatomy history
            const totalScore = data.reduce((sum: number, item: any) => sum + (parseFloat(item.average_score) || 0), 0);
            const averageScore = data.length > 0 ? Math.round((totalScore / data.length) * 100) / 100 : 0;
            
            return {
                count: data.length,
                completed: data.length, // All anatomy sessions are considered completed
                averageScore: averageScore,
                data: data
            };
        } catch (error) {
            console.error('Error loading anatomy history:', error);
            return { count: 0, completed: 0, averageScore: 0, data: [] };
        }
    }

    async loadSheetHistory(): Promise<any> {
        try {
            // Get user ID from localStorage
            const userData = localStorage.getItem('user');
            const user = userData ? JSON.parse(userData) : null;
            const userId = user?.ID_USER;

            if (!userId) {
                return { count: 0, completed: 0, averageScore: 0, data: [] };
            }

            // Get sheet answers for this user
            const response = await this.userService.getSheetAnswer({ user_id: userId });
            const data = response.data || [];
            
            // Filter completed items (those with scores)
            const completedItems = data.filter((item: any) => item.score !== null && item.score !== undefined);
            const avgScore = completedItems.length > 0 ? 
                Math.round((completedItems.reduce((sum: number, item: any) => sum + (parseFloat(item.score) || 0), 0) / completedItems.length) * 100) / 100 : 0;
            
            return {
                count: data.length,
                completed: completedItems.length,
                averageScore: avgScore,
                data: data
            };
        } catch (error) {
            console.error('Error loading sheet history:', error);
            return { count: 0, completed: 0, averageScore: 0, data: [] };
        }
    }

    async loadSimulationHistory(): Promise<any> {
        try {
            // Get user ID from localStorage
            const userData = localStorage.getItem('user');
            const user = userData ? JSON.parse(userData) : null;
            const userId = user?.ID_USER;

            if (!userId) {
                return { count: 0, completed: 0, averageScore: 0, data: [] };
            }

            // Get simulation packages
            const packageResponse = await this.userService.getPackageSimulation();
            const packages = packageResponse.data || [];
            
            let totalSimulations = 0;
            let completedSimulations = 0;
            let totalScore = 0;
            let scoreCount = 0;

            // For each package, get user answers
            for (const pkg of packages) {
                try {
                    const answerResponse = await this.userService.simulationUserAnswer({
                        user_id: userId,
                        package_id: pkg.id
                    });
                    
                    const userAnswers = answerResponse.data || [];
                    if (userAnswers.length > 0) {
                        totalSimulations++;
                        completedSimulations++;
                        
                        // Calculate average score for this package
                        const packageScore = userAnswers.reduce((sum: number, answer: any) => 
                            sum + (parseFloat(answer.SKOR) || 0), 0) / userAnswers.length;
                        
                        totalScore += packageScore;
                        scoreCount++;
                    }
                } catch (error) {
                    console.log('No answers found for package:', pkg.id);
                }
            }
            
            return {
                count: totalSimulations,
                completed: completedSimulations,
                averageScore: scoreCount > 0 ? Math.round((totalScore / scoreCount) * 100) / 100 : 0,
                data: packages
            };
        } catch (error) {
            console.error('Error loading simulation history:', error);
            return { count: 0, completed: 0, averageScore: 0, data: [] };
        }
    }

    calculateOverallAverage(dataArray: any[]): number {
        let totalScore = 0;
        let totalCount = 0;
        
        dataArray.forEach(data => {
            if (data.count > 0) {
                totalScore += data.averageScore * data.completed;
                totalCount += data.completed;
            }
        });
        
        return totalCount > 0 ? Math.round(totalScore / totalCount) : 0;
    }

    async loadRecentActivities() {
        try {
            const userData = localStorage.getItem('user');
            const user = userData ? JSON.parse(userData) : null;
            const userId = user?.ID_USER;

            if (!userId) {
                this.recentActivities = [];
                return;
            }

            this.recentActivities = [];

            // Load recent anatomy activities
            try {
                const anatomyResponse = await this.userService.getAnatomyAnswers({ user_id: userId });
                const anatomyData = anatomyResponse.data || [];
                
                // Get the latest 2 anatomy activities
                const recentAnatomy = anatomyData
                    .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .slice(0, 2);

                for (const activity of recentAnatomy) {
                    this.recentActivities.push({
                        type: 'anatomy',
                        title: 'Menyelesaikan Ujian Anatomi',
                        description: activity.anatomy_name || 'Pembelajaran Anatomi',
                        date: new Date(activity.created_at),
                        score: Math.round(activity.average_score || 0)
                    });
                }
            } catch (error) {
                console.log('No anatomy activities found');
            }

            // Load recent sheet activities
            try {
                const sheetResponse = await this.userService.getSheetAnswer({ user_id: userId });
                const sheetData = sheetResponse.data || [];
                
                // Get the latest 2 sheet activities with scores
                const recentSheets = sheetData
                    .filter((item: any) => item.score !== null && item.score !== undefined)
                    .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .slice(0, 2);

                for (const activity of recentSheets) {
                    this.recentActivities.push({
                        type: 'sheet',
                        title: 'OSCE Sheet',
                        description: activity.sheet_case_name || 'Lembar OSCE',
                        date: new Date(activity.created_at),
                        score: Math.round(parseFloat(activity.score) || 0)
                    });
                }
            } catch (error) {
                console.log('No sheet activities found');
            }

            // Load recent simulation activities
            try {
                const packageResponse = await this.userService.getPackageSimulation();
                const packages = packageResponse.data || [];
                
                let simulationActivities = [];
                
                for (const pkg of packages.slice(0, 3)) { // Check latest 3 packages
                    try {
                        const answerResponse = await this.userService.simulationUserAnswer({
                            user_id: userId,
                            package_id: pkg.id
                        });
                        
                        const userAnswers = answerResponse.data || [];
                        if (userAnswers.length > 0) {
                            const avgScore = userAnswers.reduce((sum: number, answer: any) => 
                                sum + (parseFloat(answer.SKOR) || 0), 0) / userAnswers.length;
                            
                            simulationActivities.push({
                                type: 'simulation',
                                title: 'Simulasi OSCE',
                                description: pkg.name || 'Paket Simulasi',
                                date: new Date(pkg.created_at || Date.now()),
                                score: Math.round(avgScore * 100) / 100
                            });
                        }
                    } catch (error) {
                        // Skip packages without answers
                    }
                }

                // Add latest 2 simulation activities
                simulationActivities
                    .sort((a: any, b: any) => b.date.getTime() - a.date.getTime())
                    .slice(0, 2)
                    .forEach(activity => this.recentActivities.push(activity));

            } catch (error) {
                console.log('No simulation activities found');
            }

            // Sort all activities by date and keep only the latest 5
            this.recentActivities = this.recentActivities
                .sort((a: any, b: any) => b.date.getTime() - a.date.getTime())
                .slice(0, 5);

        } catch (error) {
            console.error('Error loading recent activities:', error);
            // Keep empty to show proper empty state
            this.recentActivities = [];
        }
    }

    // --- Sections settings methods ---
    async loadUserSections() {
        try {
            this.sectionsLoading = true;
            const userData = localStorage.getItem('user');
            const user = userData ? JSON.parse(userData) : null;
            const userId = user?.ID_USER || this.userProfile.userId;
            if (!userId) {
                this.sectionsList = [];
                return;
            }

            const res = await this.userService.getSections(String(userId));
            console.log('getSections response:', res);
            this.sectionsList = Array.isArray(res.data) ? res.data : [];

            // initialize selectedSectionIds from profile
            if (this.userProfile.listSections && Array.isArray(this.userProfile.listSections)) {
                this.selectedSectionIds = [...this.userProfile.listSections];
            } else {
                this.selectedSectionIds = [];
            }

            // initialize selectedCustomTimers from section data (convert seconds -> minutes)
            for (const s of this.sectionsList) {
                const id = String(s.ID);
                const customSec = s.TIMER_CUSTOM || s.TIMER_CUSTOM_SECONDS || s.TIMER_CUSTOM_SEC;
                const n = Number(customSec || 0);
                this.selectedCustomTimers[id] = n && !isNaN(n) && n > 0 ? Math.round(n / 60) : 0;
            }

            // Autofill global custom timer if all sections share the same TIMER_CUSTOM
            const uniques = new Set<number>();
            for (const id of Object.keys(this.selectedCustomTimers)) {
                uniques.add(this.selectedCustomTimers[id] || 0);
            }
            // remove zeros when determining uniformity (only consider set values)
            const nonZero = Array.from(uniques).filter(v => v > 0);
            if (nonZero.length === 1) {
                this.selectedCustomTimerAll = nonZero[0];
                this.customTimerMixed = false;
            } else if (nonZero.length === 0) {
                // no custom timers present
                this.selectedCustomTimerAll = 0;
                this.customTimerMixed = false;
            } else {
                // varied values
                this.selectedCustomTimerAll = 0;
                this.customTimerMixed = true;
            }
        } catch (error) {
            console.error('Failed to load sections:', error);
            this.sectionsList = [];
        } finally {
            this.sectionsLoading = false;
        }
    }

    applyCustomTimerToAll() {
        const mins = Number(this.selectedCustomTimerAll) || 0;
        for (const s of this.sectionsList) {
            this.selectedCustomTimers[String(s.ID)] = mins;
        }
        this.messageService.add({ severity: 'success', summary: 'Terapkan', detail: `Timer custom ${mins} menit diterapkan ke semua section` });
    }


    isSectionSelected(id: any): boolean {
        return this.selectedSectionIds.includes(String(id));
    }

    toggleSectionSelection(id: any) {
        const sid = String(id);
        const idx = this.selectedSectionIds.indexOf(sid);
        if (idx === -1) this.selectedSectionIds.push(sid);
        else this.selectedSectionIds.splice(idx, 1);
    }

    async saveSectionSettings() {
        try {
            this.savingSections = true;
            const currentUserRaw = localStorage.getItem('user');
            const currentUser = currentUserRaw ? JSON.parse(currentUserRaw) : null;
            if (!currentUser) throw new Error('User not found');

            const payload = {
                ID_USER: currentUser.ID_USER,
                USERNAME: currentUser.USERNAME,
                LIST_SECTION: JSON.stringify(this.selectedSectionIds),
                UPDATED_BY: currentUser.USERNAME || currentUser.FULLNAME || 'user'
            };

            // Use single TIMER_CUSTOM value (seconds) derived from the global minutes input
            const globalMins = Number(this.selectedCustomTimerAll) || 0;
            const globalSecs = Math.round(globalMins * 60);
            (payload as any).TIMER_CUSTOM = globalSecs;

            // Also build a per-section map for debugging (not sent to server)
            const timerCustomMap: { [id: string]: number } = {};
            for (const sid of this.selectedSectionIds) {
                // prefer explicit per-section value if set, otherwise use global
                const mins = Number(this.selectedCustomTimers[String(sid)]) || globalMins || 0;
                timerCustomMap[String(sid)] = Math.round(mins * 60);
            }

            console.log('editUser payload:', payload);

            const res = await this.userService.editUser(payload);
            console.log('editUser response:', res);
            console.log('TIMER_CUSTOM sent (secs):', globalSecs);
            console.log('selectedCustomTimers (mins):', this.selectedCustomTimers);
            console.log('timerCustomMap (secs, debug):', timerCustomMap);
            if (res && res.status) {
                // update local profile and localStorage
                this.userProfile.listSections = [...this.selectedSectionIds];
                const updatedUser = { ...currentUser, LIST_SECTION: JSON.stringify(this.selectedSectionIds) };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Pengaturan section disimpan' });
            } else {
                throw new Error(res?.message || 'Gagal menyimpan');
            }
        } catch (error) {
            console.error('Error saving sections:', error);
            this.messageService.add({ severity: 'error', summary: 'Gagal', detail: 'Tidak dapat menyimpan pengaturan section' });
        } finally {
            this.savingSections = false;
        }
    }

    cancelSectionChanges() {
        // reset selection to profile value
        this.selectedSectionIds = Array.isArray(this.userProfile.listSections) ? [...this.userProfile.listSections] : [];
    }

    onSectionToggle(checked: boolean, id: any) {
        const sid = String(id);
        if (checked) {
            if (!this.selectedSectionIds.includes(sid)) this.selectedSectionIds.push(sid);
        } else {
            this.selectedSectionIds = this.selectedSectionIds.filter(x => x !== sid);
        }
    }

    getSectionNameById(id: string) {
        const s = this.sectionsList.find((x: any) => String(x.ID) === String(id));
        return s ? (s.NAME_STASIUN || s.NAMA_STASIUN || s.NAME || 'Section') : id;
    }

    ngAfterViewChecked(): void {
        // Update indeterminate state for category 'select all' checkboxes
        if (!this.categoryAllCheckboxes) return;
        this.categoryAllCheckboxes.forEach((el: ElementRef<HTMLInputElement>) => {
            try {
                const native = el.nativeElement;
                const cat = native.getAttribute('data-cat');
                if (!cat) return;
                const some = this.getSectionsByCategory(cat).some(s => this.isSectionSelected(s.ID));
                const all = this.isAllSelectedInCategory(cat);
                native.indeterminate = some && !all;
            } catch (e) {
                // ignore
            }
        });
    }

    // Return ordered unique category list across sections
    getCategoryList(): string[] {
        const cats: string[] = [];
        for (const s of this.sectionsList || []) {
            if (!Array.isArray(s.CATEGORY)) continue;
            for (const c of s.CATEGORY) {
                if (!cats.includes(c)) cats.push(c);
            }
        }
        return cats;
    }

    getSectionsByCategory(cat: string): any[] {
        if (!this.sectionsList) return [];
        return this.sectionsList.filter(s => Array.isArray(s.CATEGORY) && s.CATEGORY.includes(cat));
    }

    isAllSelectedInCategory(cat: string): boolean {
        const secs = this.getSectionsByCategory(cat);
        if (!secs.length) return false;
        return secs.every(s => this.isSectionSelected(s.ID));
    }

    toggleSelectAllInCategory(cat: string) {
        const secs = this.getSectionsByCategory(cat).map(s => String(s.ID));
        const allSelected = this.isAllSelectedInCategory(cat);
        if (allSelected) {
            // remove all in this category
            this.selectedSectionIds = this.selectedSectionIds.filter(id => !secs.includes(id));
        } else {
            // add those that are missing
            for (const id of secs) {
                if (!this.selectedSectionIds.includes(id)) this.selectedSectionIds.push(id);
            }
        }
    }

    getInitials(): string {
        if (this.userProfile.name) {
            return this.userProfile.name
                .split(' ')
                .map(name => name.charAt(0))
                .join('')
                .toUpperCase()
                .substring(0, 2);
        }
        return 'U';
    }

    getRoleSeverity(role: string): string {
        switch (role.toLowerCase()) {
            case 'admin':
                return 'danger';
            case 'tutor':
            case 'teacher':
                return 'warning';
            case 'student':
            case 'peserta':
                return 'info';
            default:
                return 'secondary';
        }
    }

    getRoleClasses(role: string): string {
        switch (role.toLowerCase()) {
            case 'admin':
                return 'bg-red-100 text-red-800';
            case 'tutor':
            case 'teacher':
                return 'bg-yellow-100 text-yellow-800';
            case 'student':
            case 'peserta':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    }

    getSectionNames(): string {
        if (this.userProfile.listSections.length === 0) {
            return 'Tidak ada akses seksi';
        }
        return `${this.userProfile.listSections.length} seksi`;
    }

    getJoinDate(): string {
        if (this.userProfile.createdAt) {
            return new Intl.DateTimeFormat('id-ID', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }).format(new Date(this.userProfile.createdAt));
        }
        return 'Tidak tersedia';
    }

    formatSecondsToMinutes(value: any): string {
        const n = Number(value || 0);
        if (!n || isNaN(n) || n <= 0) return '-';
        const mins = Math.round(n / 60);
        return `${mins} menit`;
    }

    getCompletionRate(): number {
        if (this.userStats.totalExams === 0) return 0;
        return Math.round((this.userStats.completedExams / this.userStats.totalExams) * 100);
    }

    getScoreClass(): string {
        const score = this.userStats.averageScore;
        if (score >= 80) return 'text-green-100';
        if (score >= 60) return 'text-yellow-100';
        return 'text-red-100';
    }

    getScoreLabel(): string {
        const score = this.userStats.averageScore;
        if (score >= 90) return 'Sangat Baik';
        if (score >= 80) return 'Baik';
        if (score >= 70) return 'Cukup';
        if (score >= 60) return 'Perlu Perbaikan';
        return 'Kurang';
    }

    getScoreColorClass(score: number): string {
        if (score >= 80) return 'text-green-600';
        if (score >= 70) return 'text-blue-600';
        if (score >= 60) return 'text-yellow-600';
        return 'text-red-600';
    }

    getScoreLevel(score: number): string {
        if (score >= 80) return 'excellent';
        if (score >= 70) return 'good';
        if (score >= 60) return 'average';
        return 'poor';
    }

    getActivityColor(type: string): string {
        switch (type) {
            case 'anatomy': return 'bg-blue-500';
            case 'sheet': return 'bg-green-500';
            case 'simulation': return 'bg-purple-500';
            default: return 'bg-gray-500';
        }
    }

    formatDate(date: Date): string {
        return new Intl.DateTimeFormat('id-ID', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    }

    formatScore(score: number): string {
        if (typeof score !== 'number' || isNaN(score)) {
            return '0';
        }
        // Round to 2 decimal places and remove trailing zeros
        return parseFloat(score.toFixed(2)).toString();
    }

    toggleEdit() {
        this.isEditing = true;
    }

    async saveProfile() {
        this.saving = true;
        try {
            // Get current user data from localStorage to get ID_USER
            const userData = localStorage.getItem('user');
            if (!userData) {
                throw new Error('No user data found');
            }
            
            const currentUser = JSON.parse(userData);
            
            // Prepare payload for editUser API
            const payload = {
                ID_USER: currentUser.ID_USER,
                USERNAME: this.userProfile.username,
                EMAIL: this.userProfile.email,
                FULLNAME: this.userProfile.name,
                INSTANSI: this.userProfile.institution || null,
                PHONE: this.userProfile.phone || null,
                UPDATED_BY: currentUser.USERNAME || 'user'
            };

            // Call editUser API
            const response = await this.userService.editUser(payload);
            
            if (response.status) {
                // Update localStorage with new data
                const updatedUser = {
                    ...currentUser,
                    FULLNAME: this.userProfile.name,
                    USERNAME: this.userProfile.username,
                    PHONE: this.userProfile.phone,
                    INSTANSI: this.userProfile.institution
                };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                
                this.originalProfile = { ...this.userProfile };
                this.isEditing = false;
                
                // Show success message
                this.messageService.add({
                    severity: 'success',
                    summary: 'Berhasil',
                    detail: 'Profil berhasil diperbarui'
                });
            } else {
                throw new Error(response.message || 'Failed to update profile');
            }
            
        } catch (error) {
            console.error('Error saving profile:', error);
            // Show error message
            this.messageService.add({
                severity: 'error',
                summary: 'Gagal',
                detail: 'Gagal menyimpan profil. Silakan coba lagi.'
            });
        } finally {
            this.saving = false;
        }
    }

    cancelEdit() {
        this.userProfile = { ...this.originalProfile };
        this.isEditing = false;
    }

    // Device Management Methods
    getDeviceName(deviceInfo: string): string {
        if (deviceInfo.includes('Windows')) {
            return 'Windows PC';
        } else if (deviceInfo.includes('Mac')) {
            return 'Mac';
        } else if (deviceInfo.includes('iPhone')) {
            return 'iPhone';
        } else if (deviceInfo.includes('iPad')) {
            return 'iPad';
        } else if (deviceInfo.includes('Android')) {
            return 'Android';
        } else if (deviceInfo.includes('Linux')) {
            return 'Linux PC';
        }
        return 'Perangkat Tidak Dikenal';
    }

    getBrowserInfo(deviceInfo: string): string {
        if (deviceInfo.includes('Chrome')) {
            return 'Google Chrome';
        } else if (deviceInfo.includes('Firefox')) {
            return 'Mozilla Firefox';
        } else if (deviceInfo.includes('Safari')) {
            return 'Safari';
        } else if (deviceInfo.includes('Edge')) {
            return 'Microsoft Edge';
        } else if (deviceInfo.includes('Opera')) {
            return 'Opera';
        }
        return 'Browser Tidak Dikenal';
    }

    getDeviceIcon(deviceInfo: string): string {
        if (deviceInfo.includes('Windows')) {
            return 'pi pi-desktop';
        } else if (deviceInfo.includes('Mac')) {
            return 'pi pi-desktop';
        } else if (deviceInfo.includes('iPhone') || deviceInfo.includes('iPad')) {
            return 'pi pi-mobile';
        } else if (deviceInfo.includes('Android')) {
            return 'pi pi-mobile';
        } else if (deviceInfo.includes('Linux')) {
            return 'pi pi-desktop';
        }
        return 'pi pi-question';
    }

    getDeviceIconClass(deviceInfo: string): string {
        if (deviceInfo.includes('Windows')) {
            return 'bg-blue-100 text-blue-600';
        } else if (deviceInfo.includes('Mac')) {
            return 'bg-gray-100 text-gray-600';
        } else if (deviceInfo.includes('iPhone') || deviceInfo.includes('iPad')) {
            return 'bg-black text-white';
        } else if (deviceInfo.includes('Android')) {
            return 'bg-green-100 text-green-600';
        } else if (deviceInfo.includes('Linux')) {
            return 'bg-orange-100 text-orange-600';
        }
        return 'bg-gray-100 text-gray-500';
    }

    isCurrentDevice(deviceId: string): boolean {
        // Method 1: Check if this device was used recently and matches current browser
        const device = this.userProfile.devices.find(d => d.DEVICE_ID === deviceId);
        if (!device) return false;

        // Debug logging
        console.log('Checking device:', {
            deviceId: deviceId,
            deviceInfo: device.DEVICE_INFO,
            lastLogin: device.LAST_LOGIN,
            currentUserAgent: navigator.userAgent
        });

        // Check if last login was recent (within last 2 hours)
        const lastLogin = new Date(device.LAST_LOGIN);
        const now = new Date();
        const diffInHours = (now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60);

        console.log('Time difference in hours:', diffInHours);

        // If login was recent and browser info matches current browser
        if (diffInHours <= 2) {
            const currentUserAgent = navigator.userAgent;
            const currentBrowser = this.getBrowserFromUserAgent(currentUserAgent);
            const currentOS = this.getOSFromUserAgent(currentUserAgent);
            const browserMatches = device.DEVICE_INFO.includes(currentBrowser);
            const osMatches = device.DEVICE_INFO.includes(currentOS);
            
            console.log('Browser/OS check:', {
                currentBrowser,
                currentOS,
                browserMatches,
                osMatches
            });
            
            if (browserMatches && osMatches) {
                console.log('Device identified as current device (recent + matching)');
                return true;
            }
        }

        // Method 2: Fallback - check if this is the most recently used device with matching browser/OS
        const currentUserAgent = navigator.userAgent;
        const currentBrowser = this.getBrowserFromUserAgent(currentUserAgent);
        const currentOS = this.getOSFromUserAgent(currentUserAgent);

        const matchingDevices = this.userProfile.devices
            .filter(d => 
                d.DEVICE_INFO.includes(currentBrowser) && 
                d.DEVICE_INFO.includes(currentOS)
            )
            .sort((a, b) => new Date(b.LAST_LOGIN).getTime() - new Date(a.LAST_LOGIN).getTime());

        console.log('Matching devices:', matchingDevices.map(d => ({
            id: d.DEVICE_ID,
            info: d.DEVICE_INFO,
            lastLogin: d.LAST_LOGIN
        })));

        // Return true if this is the most recent device with matching characteristics
        const isCurrentByFallback = matchingDevices.length > 0 && matchingDevices[0].DEVICE_ID === deviceId;
        if (isCurrentByFallback) {
            console.log('Device identified as current device (fallback method)');
        }
        
        return isCurrentByFallback;
    }

    getCurrentDeviceId(): string {
        // Try to find the most recent device that matches current browser
        const currentUserAgent = navigator.userAgent;
        const currentBrowser = this.getBrowserFromUserAgent(currentUserAgent);
        const currentOS = this.getOSFromUserAgent(currentUserAgent);

        // Find device that matches current browser and OS, sorted by most recent login
        const matchingDevice = this.userProfile.devices
            .filter(device => 
                device.DEVICE_INFO.includes(currentBrowser) && 
                device.DEVICE_INFO.includes(currentOS)
            )
            .sort((a, b) => new Date(b.LAST_LOGIN).getTime() - new Date(a.LAST_LOGIN).getTime())[0];

        return matchingDevice ? matchingDevice.DEVICE_ID : '';
    }

    private getBrowserFromUserAgent(userAgent: string): string {
        if (userAgent.includes('Chrome')) return 'Chrome';
        if (userAgent.includes('Firefox')) return 'Firefox';
        if (userAgent.includes('Safari')) return 'Safari';
        if (userAgent.includes('Edge')) return 'Edge';
        if (userAgent.includes('Opera')) return 'Opera';
        return 'Unknown';
    }

    private getOSFromUserAgent(userAgent: string): string {
        if (userAgent.includes('Windows')) return 'Windows';
        if (userAgent.includes('Mac')) return 'Mac';
        if (userAgent.includes('Linux')) return 'Linux';
        if (userAgent.includes('Android')) return 'Android';
        if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';
        return 'Unknown';
    }

    formatLastLogin(lastLogin: string): string {
        const date = new Date(lastLogin);
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInMinutes < 1) {
            return 'Baru saja';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes} menit lalu`;
        } else if (diffInHours < 24) {
            return `${diffInHours} jam lalu`;
        } else if (diffInDays === 1) {
            return 'Kemarin';
        } else if (diffInDays < 7) {
            return `${diffInDays} hari lalu`;
        } else {
            return new Intl.DateTimeFormat('id-ID', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        }
    }

    removeDevice(device: any) {
        this.confirmationService.confirm({
            message: `Apakah Anda yakin ingin mengeluarkan perangkat "${this.getDeviceName(device.DEVICE_INFO)}"? Perangkat ini tidak akan dapat mengakses akun Anda.`,
            header: 'Konfirmasi Hapus Perangkat',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
            acceptLabel: 'Ya, Keluarkan',
            rejectLabel: 'Batal',
            accept: async () => {
                try {
                    // Call API to remove device
                    const response = await this.userService.logout({
                        ID_USER: this.userProfile.userId,
                        DEVICE_ID: device.DEVICE_ID
                    });

                    if (response.status) {
                        // Remove device from local list
                        this.userProfile.devices = this.userProfile.devices.filter(
                            d => d.DEVICE_ID !== device.DEVICE_ID
                        );

                        this.messageService.add({
                            severity: 'success',
                            summary: 'Berhasil',
                            detail: 'Perangkat berhasil dikeluarkan dari akun Anda'
                        });
                    } else {
                        throw new Error(response.message || 'Failed to remove device');
                    }
                } catch (error) {
                    console.error('Error removing device:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Gagal',
                        detail: 'Gagal mengeluarkan perangkat. Silakan coba lagi.'
                    });
                }
            }
        });
    }

    logoutFromAllDevices() {
        this.confirmationService.confirm({
            message: 'Apakah Anda yakin ingin keluar dari semua perangkat? Anda perlu masuk kembali di perangkat ini setelah konfirmasi.',
            header: 'Konfirmasi Keluar dari Semua Perangkat',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
            acceptLabel: 'Ya, Keluar dari Semua',
            rejectLabel: 'Batal',
            accept: async () => {
                try {
                    // Call API to logout from all devices
                    const response = await this.userService.deleteUserDevice({
                        ID_USER: this.userProfile.userId,
                        LOGOUT_ALL: true
                    });

                    if (response.status) {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Berhasil',
                            detail: 'Berhasil keluar dari semua perangkat. Anda akan dialihkan ke halaman login.'
                        });

                        const token = localStorage.getItem('token');
                        if (token) {
                            localStorage.removeItem('token');
                            localStorage.removeItem('isLoggedIn');
                            localStorage.removeItem('user');
                            window.location.href = 'https://oscemediko.id/';
                        }
                    } else {
                        throw new Error(response.message || 'Failed to logout from all devices');
                    }
                } catch (error) {
                    console.error('Error logging out from all devices:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Gagal',
                        detail: 'Gagal keluar dari semua perangkat. Silakan coba lagi.'
                    });
                }
            }
        });
    }
}